import { useCallback } from 'react'
import { useAuthStore } from '@/shared/stores/auth.store'
import { useVoiceRecordingStore } from '@/shared/stores/voice-recording.store'

const DRAFT_KEY = (sessionId: string) => `draft:${sessionId}`

/** Objets impératifs NON sérialisables d'un enregistrement en cours pour UNE session. */
interface ImperativeVoiceState {
  mediaRecorder: MediaRecorder | null
  chunks: Blob[]
  timerId: ReturnType<typeof setInterval> | null
  lastBlob: { blob: Blob; mimeType: string } | null
}

/**
 * Map MODULE-SCOPE (hors React), keyée par sessionId. Contient le MediaRecorder, les chunks
 * audio et le timer d'un enregistrement en cours — des objets qui ne se sérialisent pas et
 * n'ont donc pas leur place dans le store Zustand (voice-recording.store.ts, qui porte
 * uniquement l'état réactif). Cette Map n'est JAMAIS détruite par un unmount de composant :
 * elle survit au remount total de ChatWidget déclenché par `key={activeTab.sessionId}` dans
 * ChatPage.tsx quand l'utilisateur change d'onglet de conversation pendant un enregistrement.
 * Voir audit #26190 (cause racine du bug micro non persistant).
 */
const imperativeState = new Map<string, ImperativeVoiceState>()

function getImperative(sessionId: string): ImperativeVoiceState {
  let state = imperativeState.get(sessionId)
  if (!state) {
    state = { mediaRecorder: null, chunks: [], timerId: null, lastBlob: null }
    imperativeState.set(sessionId, state)
  }
  return state
}

function clearImperativeTimer(imp: ImperativeVoiceState) {
  if (imp.timerId) {
    clearInterval(imp.timerId)
    imp.timerId = null
  }
}

// Signature INCHANGÉE (appelée par InputBar.tsx) — `onTranscription` n'est plus invoqué DANS
// ce hook (voir le point d'attention dans transcribeBlob ci-dessous : écriture localStorage
// directe à la place), le paramètre reste dans le contrat pour ne pas casser l'appelant.
export function useVoiceInput(sessionId: string, _onTranscription: (text: string) => void) {
  const recording = useVoiceRecordingStore(s => s.getRecording(sessionId))
  const setRecording = useVoiceRecordingStore(s => s.setRecording)

  const { isRecording, isTranscribing, recordingDuration, lastError, canRetry } = recording

  const transcribeBlob = useCallback(async (blob: Blob, mimeType: string) => {
    const imp = getImperative(sessionId)
    // canRetry:false pendant la transcription (comme le calcul derive de la version d'origine
    // `lastBlobRef.current !== null && !isTranscribing`) — masque le bouton "Reessayer" tant
    // qu'une tentative est en cours, y compris un retry.
    setRecording(sessionId, { isTranscribing: true, lastError: null, canRetry: false })
    try {
      const formData = new FormData()
      const ext = mimeType.includes('mp4') ? 'mp4' : 'webm'
      formData.append('audio', blob, `recording.${ext}`)

      const token = useAuthStore.getState().accessToken
      const res = await fetch('/api/voice/transcribe', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      })

      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: 'Transcription failed' })) as { error?: string }
        throw new Error(body.error ?? `HTTP ${res.status}`)
      }

      const { text } = await res.json() as { text: string }

      if (text.trim()) {
        // POINT D'ATTENTION (miniplan/audit #26190) : écrire DIRECTEMENT dans localStorage
        // plutôt que d'appeler `onTranscription` (closure capturée au moment du
        // `startRecording`). Si l'utilisateur a changé d'onglet de conversation pendant
        // l'enregistrement, cette closure référence une instance d'InputBar potentiellement
        // déjà démontée pour une AUTRE session — écrire la clé de draft de la BONNE session
        // directement est la source de vérité, indépendante du cycle de vie React.
        // InputBar relit systématiquement DRAFT_KEY(sessionId) à son montage (useState
        // initializer, InputBar.tsx) : le texte apparaît dès qu'on revient sur cet onglet.
        // Si InputBar est resté monté sur cette session pendant tout l'enregistrement (pas de
        // switch d'onglet), c'est le rechargement déclenché par le passage
        // isTranscribing:true->false (effet ajouté dans InputBar.tsx) qui relit ce draft.
        const prevDraft = localStorage.getItem(DRAFT_KEY(sessionId)) ?? ''
        const nextDraft = (prevDraft ? `${prevDraft} ` : '') + text.trim()
        localStorage.setItem(DRAFT_KEY(sessionId), nextDraft)
        imp.lastBlob = null
        setRecording(sessionId, { canRetry: false })
        return
      }

      // Empty transcription = failure
      imp.lastBlob = { blob, mimeType }
      setRecording(sessionId, { lastError: 'Transcription vide. Reessayez.', canRetry: true })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur inconnue'
      imp.lastBlob = { blob, mimeType }
      setRecording(sessionId, { lastError: message, canRetry: true })
    } finally {
      setRecording(sessionId, { isTranscribing: false })
    }
  }, [sessionId, setRecording])

  const startRecording = useCallback(async () => {
    const imp = getImperative(sessionId)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          channelCount: 1,
        },
      })

      // Choose supported mimeType — audio/mp4 first (Safari iOS produces valid mp4,
      // Whisper rejects audio/webm;codecs=opus on some configurations)
      const mimeType = MediaRecorder.isTypeSupported('audio/mp4')
        ? 'audio/mp4'
        : MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm'

      const recorder = new MediaRecorder(stream, { mimeType })
      imp.chunks = []

      recorder.ondataavailable = (e: BlobEvent) => {
        if (e.data.size > 0) imp.chunks.push(e.data)
      }

      // onstop se déclenche aussi bien sur un stop() manuel (stopRecording) que sur un
      // arrêt DÉCLENCHÉ PAR LE NAVIGATEUR (ex: Safari/WebKit coupe intentionnellement les
      // tracks getUserMedia quand l'onglet passe en arrière-plan). Dans ce dernier cas,
      // stopRecording() n'est jamais appelé — sans ce reset ici, isRecording restait
      // bloqué à true et le chrono continuait indéfiniment, laissant le bouton micro figé
      // dans un état "enregistrement" alors que l'enregistrement réel était déjà terminé.
      recorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop())
        clearImperativeTimer(imp)
        setRecording(sessionId, { isRecording: false })
        const blob = new Blob(imp.chunks, { type: mimeType })
        void transcribeBlob(blob, mimeType)
      }

      // Traçabilité : un track qui se termine sans stop() explicite de notre part vient
      // forcément d'une décision du navigateur/OS (permission révoquée, périphérique
      // déconnecté, ou coupure de capture en arrière-plan côté WebKit). On ne tente rien
      // pour "relancer" la capture — c'est hors de portée du JS — onstop ci-dessus gère
      // déjà le retour à un état UI propre et la transcription du contenu déjà capturé.
      stream.getAudioTracks().forEach(track => {
        track.onended = () => {
          console.debug('[voice-input] audio track ended (navigateur/OS a coupé la capture)')
        }
      })

      imp.mediaRecorder = recorder
      recorder.start(100) // collect chunks every 100ms
      setRecording(sessionId, { isRecording: true, recordingDuration: 0, lastError: null })
      imp.timerId = setInterval(() => {
        const current = useVoiceRecordingStore.getState().getRecording(sessionId)
        setRecording(sessionId, { recordingDuration: current.recordingDuration + 1 })
      }, 1000)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Microphone non disponible'
      setRecording(sessionId, { lastError: message })
    }
  }, [sessionId, setRecording, transcribeBlob])

  const stopRecording = useCallback(() => {
    const imp = getImperative(sessionId)
    if (imp.mediaRecorder?.state === 'recording') {
      imp.mediaRecorder.stop()
    }
    clearImperativeTimer(imp)
    setRecording(sessionId, { isRecording: false })
  }, [sessionId, setRecording])

  const retryTranscription = useCallback(() => {
    const imp = getImperative(sessionId)
    if (imp.lastBlob) {
      void transcribeBlob(imp.lastBlob.blob, imp.lastBlob.mimeType)
    }
  }, [sessionId, transcribeBlob])

  const clearError = useCallback(() => {
    const imp = getImperative(sessionId)
    imp.lastBlob = null
    setRecording(sessionId, { lastError: null, canRetry: false })
  }, [sessionId, setRecording])

  return {
    isRecording,
    isTranscribing,
    recordingDuration,
    lastError,
    canRetry,
    startRecording,
    stopRecording,
    retryTranscription,
    clearError,
  }
}
